import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Radio from '@mui/material/Radio';
import Typography from "@mui/material/Typography";
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconProvider from "../../../components/icons/IconProvider";
import { StepperContext } from '../../../context/StepperProvider';
import { IVehicleContext, VehicleContext } from "../../../context/VehicleProvider";
import { Vehicle } from '../../../data/vehicle';
import { ConnectorList, FastPlug } from '../../../data/vehicleOptions';
import { useVehicleByIdQuery, useVehiclesQuery } from '../../../hooks/useVehiclesQuery';
import "./VehicleOptionsDialog.css";

export default function VehicleOptionsDialog() {
    const { t } = useTranslation()
    const { setNextDisabled } = useContext(StepperContext)
    const { basicConnectors, powerMaxStandard, optionalConnectors, vehicle, powerMaxFast, capacityFull,
        setCapacityFull, setPowerMaxFast, setVehicle, setPowerMaxStandard, setBasicConnectors, setOptionalConnectors, clearVehicle } = useContext(VehicleContext) as IVehicleContext;
    const [refetchVehicleDetails, setRefetchVehicleDetails] = useState(false)
    const vehicleModelQuery = useVehicleByIdQuery(vehicle?.Id, refetchVehicleDetails)
    const vehiclesQuery = useVehiclesQuery()
    const [optionsDialogOpen, setOptionsDialogOpen] = useState(false)
    const [connectorOptionsOpen, setConnectorOptionsOpen] = useState(false)
    const [batteryCapacityOptionsOpen, setBatteryCapacityOptionsOpen] = useState(false)
    const [powerMaxFastOptionsOpen, setPowerMaxFastOptionsOpen] = useState(false)
    const [powerMaxStandardOptionsOpen, setPowerMaxStandardOptionsOpen] = useState(false)
    const [connectorOptions, setConnectorOptions] = useState<string[]>(optionalConnectors || []);
    const [sameProfileVehicle, setSameProfileVehicle] = useState<Vehicle[]>([])
    const [batteryCapacity, setBatteryCapacity] = useState<Vehicle[]>([])
    const [powerMaxFastOptions, setPowerMaxFastOptions] = useState<number[]>([])
    const [powerStandardOptions, setPowerStandardOptions] = useState<number[]>([])
    const [selectedCapacityFull, setSelectedCapacityFull] = useState<[number, number] | undefined>(capacityFull)
    const [selectedPowerFast, setSelectedPowerFast] = useState<number | undefined>(powerMaxFast)
    const [selectedPowerStandard, setSelectedPowerStandard] = useState<number | undefined>(powerMaxStandard)

    useEffect(() => {
        if (vehiclesQuery.isSuccess || vehicleModelQuery.isSuccess) {
            if (vehicleModelQuery.error || vehiclesQuery.error) {
                setNextDisabled(true)
                clearVehicle();
                return
            }

            if (typeof vehicle !== 'undefined' && vehiclesQuery.data && vehicleModelQuery.data) {
                // Battery capacity, fast, standard options
                getVehicleOptions(vehicle, vehiclesQuery.data)
                getVehicleBasicConnectors(vehicleModelQuery.data.data)
                setNextDisabled(false)
            }
        }
    }, [vehiclesQuery.isSuccess, vehicleModelQuery.isSuccess, vehicle, selectedCapacityFull, selectedPowerFast])

    useEffect(() => {
        if (capacityFull?.[0] !== selectedCapacityFull?.[0] && capacityFull?.[1] !== selectedCapacityFull?.[1]) {
            setSelectedPowerFast(undefined)
            setPowerMaxFast(undefined)
        }

        if (typeof vehicle === 'undefined') {
            setConnectorOptions([])
            setSameProfileVehicle([])
            setPowerStandardOptions([])
            setPowerMaxFastOptions([])
            setBatteryCapacity([])
            setSelectedCapacityFull(undefined)
            setSelectedPowerFast(undefined)
            setSelectedPowerStandard(undefined)
            setVehicle(undefined)
            setCapacityFull(undefined)
            setPowerMaxFast(undefined)
            setPowerMaxStandard(undefined)
            setRefetchVehicleDetails(false)
        }
    }, [vehicle, selectedCapacityFull])

    const isSameVehicle = (v: Vehicle, specifiedVehicle: Vehicle) => {
        if (v.Brand === specifiedVehicle.Brand && v.Model === specifiedVehicle.Model && v.Version === specifiedVehicle.Version) {
            return true
        }

        return false
    }

    const selectDefaultVehicle = (selectedCapacity: null | undefined | Vehicle, capacityOptions: Vehicle[]) => {
        // First render, select first vehicle in sorted array by default
        if ((typeof selectedCapacityFull === 'undefined' && typeof capacityFull === 'undefined') && capacityOptions.length > 0) {
            selectedCapacity = capacityOptions.find((vehicle: Vehicle) => vehicle.Id === capacityOptions[0].Id)
            setSelectedCapacityFull([capacityOptions[0].CapacityFull!, capacityOptions[0].RangeReal!])
            return selectedCapacity;
        } else if (typeof selectedCapacityFull !== 'undefined') {
            selectedCapacity = capacityOptions.find((v: Vehicle) => v.CapacityFull === selectedCapacityFull[0] && v.RangeReal === selectedCapacityFull[1])
            if (typeof selectedCapacity !== 'undefined') {
                setSelectedCapacityFull([selectedCapacity.CapacityFull!, selectedCapacity.RangeReal!])
            }
            return selectedCapacity
        }
    }

    const getVehicleOptions = (specifiedVehicle: Vehicle | undefined, vehicleModels: Vehicle[]) => {
        if (specifiedVehicle && vehicleModels.length > 0) {
            let vehicles: Vehicle[] = vehicleModels.filter(v => isSameVehicle(v, specifiedVehicle))
            let capacityOptions = Array.from(new Map(vehicles.map((vehicle) => [vehicle.RangeReal!, vehicle])).values())

            setSameProfileVehicle(vehicles)

            if (capacityOptions.length > 0) {
                let selectedCapacity: null | undefined | Vehicle = {} as Vehicle
                let fastOptions: number[] = []
                let standardOptions: number[] = []

                capacityOptions.sort((a, b) => (a.CapacityFull! === b.CapacityFull!) ? b.RangeReal! - a.RangeReal! : b.CapacityFull! - a.CapacityFull!)
                setBatteryCapacity(capacityOptions) // Options for battery capacity                
                selectedCapacity = selectDefaultVehicle(selectedCapacity, capacityOptions);

                if (typeof selectedCapacity !== 'undefined') {
                    const sameCapacityVehicles: Vehicle[] = Array.from(vehicles.filter((v: Vehicle) => v.CapacityFull === selectedCapacity?.CapacityFull && v.RangeReal === selectedCapacity?.RangeReal))
                    let allVehiclesFastOptions = []

                    // If a brand-model-version vehicle has the same capacity and range, the option is displayed once
                    // Getting all fast options from vehicles with the same capacity and range
                    if (sameCapacityVehicles.length > 1) {
                        allVehiclesFastOptions = Array.from(new Map(sameCapacityVehicles.map(v => [v.PowerMaxFast, v])).values())

                        if (allVehiclesFastOptions.length > 0) {
                            allVehiclesFastOptions.forEach((v: Vehicle) => {
                                if (typeof v.PowerMaxFast !== 'undefined' && v.PowerMaxFast !== null && v.PowerMaxFast > 0) {
                                    fastOptions = (!v.FastChargeOptional) ? [...fastOptions, v.PowerMaxFast] : [...fastOptions, 0, v.PowerMaxFast] // 0 rendered as "No fast charge option"
                                }
                            })
                        }
                    } else if (typeof selectedCapacity.PowerMaxFast !== 'undefined' && selectedCapacity.PowerMaxFast !== null && selectedCapacity.PowerMaxFast > 0) {
                        fastOptions = (!selectedCapacity.FastChargeOptional) ? [...fastOptions, selectedCapacity.PowerMaxFast] : [...fastOptions, 0, selectedCapacity.PowerMaxFast]
                    }

                    if (fastOptions.length > 0) {
                        fastOptions.sort((a, b) => (a - b))
                    }

                    if (capacityOptions.length > 1 && selectedCapacity.PowerMaxStandard!.length > 0) {
                        standardOptions = selectedCapacity.PowerMaxStandard!.sort((a, b) => b - a)
                    } else if (typeof selectedPowerFast !== 'undefined') {
                        standardOptions = vehicles.find((v: Vehicle) => v.PowerMaxFast === selectedPowerFast)?.PowerMaxStandard!.sort((a, b) => b - a)!
                    }

                    setPowerMaxFastOptions(fastOptions)
                    setPowerStandardOptions(standardOptions)

                    if (typeof selectedPowerFast === 'undefined' && typeof powerMaxFast === 'undefined') {
                        setSelectedPowerFast(fastOptions[0])
                    } else if (typeof powerMaxFast !== 'undefined' && typeof selectedPowerFast === 'undefined') {
                        setSelectedPowerFast(powerMaxFast)
                    }
                    if (typeof powerMaxStandard === 'undefined' && typeof selectedPowerStandard === 'undefined' && standardOptions.length > 0) {
                        setSelectedPowerStandard(standardOptions[0])
                    } else if (typeof powerMaxStandard !== 'undefined' && typeof selectedPowerStandard === 'undefined') {
                        setSelectedPowerStandard(powerMaxStandard)
                    }

                    updateSelectedVehicle()
                }
            }
        }
    }

    const isSameSelectedVehicle = (v: Vehicle) => {
        if (typeof selectedCapacityFull !== 'undefined') {
            if (v.CapacityFull === selectedCapacityFull[0] && v.RangeReal === selectedCapacityFull[1]) {
                if ((v.PowerMaxFast === selectedPowerFast)
                    || (typeof selectedPowerStandard !== 'undefined' && selectedPowerFast === 0 && v.FastChargeOptional && v.PowerMaxStandard?.includes(selectedPowerStandard))
                    || (typeof selectedPowerFast === 'undefined' && typeof selectedPowerStandard !== 'undefined' && v.PowerMaxStandard?.includes(selectedPowerStandard))) {
                    return true;
                }
            }
        }

        return false;
    }

    const updateSelectedVehicle = () => {
        const selectedVehicle: undefined | Vehicle = sameProfileVehicle.find((v: Vehicle) => {
            if (isSameSelectedVehicle(v)) {
                return v
            }

            return null
        })

        setOptionalConnectors(connectorOptions.length > 0 ? connectorOptions : [])

        if (selectedVehicle) {
            setCapacityFull(selectedCapacityFull)
            setPowerMaxFast(selectedPowerFast)
            setPowerMaxStandard(selectedPowerStandard)
            setVehicle(selectedVehicle)
            setRefetchVehicleDetails(true)
        }
    }

    const getVehicleBasicConnectors = (specifiedVehicle: Vehicle) => {
        const standardIndex = Object.keys(ConnectorList).indexOf(specifiedVehicle.StandardChargeDetails!.Standard);
        const standardPlug = Object.values(ConnectorList)[standardIndex];
        const fastPlugOptional = specifiedVehicle.FastChargeDetails?.Optional;
        let basicConnectors = basicConnectorsCreator(standardPlug);
        let fastPlug = null;
        let fastIndex;

        if (specifiedVehicle.FastChargeDetails?.Standard === "CCS Supercharger" || specifiedVehicle.FastChargeDetails?.Standard === "CCS") {
            fastIndex = Object.keys(ConnectorList).indexOf("Combo CCS");
        }
        else {
            fastIndex = Object.keys(ConnectorList).indexOf(specifiedVehicle.FastChargeDetails!.Standard);
        }

        if (fastIndex !== -1) {
            fastPlug = Object.values(ConnectorList)[fastIndex];
        }

        if (fastPlug !== null) {
            basicConnectors = basicConnectorsCreator(standardPlug, fastPlug, fastPlugOptional)
        }
        setBasicConnectors(basicConnectors)
    }

    const handleConnectorOptionsToggle = (value: string) => () => {
        const index = connectorOptions.indexOf(value);

        if (index !== -1) {
            if (connectorOptions.length === 1) {
                setConnectorOptions([])
            }
            else {
                setConnectorOptions([...connectorOptions.slice(0, index), ...connectorOptions.slice(index + 1)])
            }
        } else {
            setConnectorOptions([...connectorOptions, value])
        }
    }

    const isConnectorOptional = (connector: string, standardPlug: any, fastPlugObject: any) => {
        if (standardPlug === connector
            || (fastPlugObject?.fastPlug === connector && fastPlugObject?.fastPlugOptional === false)
            || (connector === "TESLA_S" || connector === ConnectorList['Type 1'])
            || ((fastPlugObject === undefined || fastPlugObject?.fastPlug === ConnectorList['Type 2']) && (connector === ConnectorList['Combo CCS'] || connector === ConnectorList['CHAdeMO']))) {
            return true;
        }

        return false;
    }

    const basicConnectorsCreator = function (inputStandardPlug: string, fastPlug?: string, fastPlugOptional?: boolean) {
        const standardPlug = inputStandardPlug

        if (fastPlug && fastPlugOptional !== undefined) {
            const fastPlugObject: FastPlug = { fastPlug, fastPlugOptional }
            return { standardPlug, fastPlugObject }
        }

        return { standardPlug }
    }

    const applyOptions = () => {
        updateSelectedVehicle()
        setOptionsDialogOpen(false)
    }

    const closeDialog = () => {
        setOptionsDialogOpen(false)
        setBatteryCapacityOptionsOpen(false)
        setPowerMaxFastOptionsOpen(false)
        setPowerMaxStandardOptionsOpen(false)
    }

    return (
        <>
            {(typeof vehicle !== 'undefined' && basicConnectors) &&
                <>
                    <Button className="rounded-button" variant="outlined" color={"secondary"} onClick={() => setOptionsDialogOpen(true)}>{t("vehicle-profile.add-options")}</Button>
                    <Dialog open={optionsDialogOpen} onClose={closeDialog}>
                        <DialogTitle textAlign={"center"} marginLeft={0}>{t("vehicle-profile.add-options")}</DialogTitle>
                        <DialogContent>
                            {
                                <>
                                    {(batteryCapacity.length > 0) && <ClickAwayListener onClickAway={() => setBatteryCapacityOptionsOpen(false)}>
                                        <List className="list-option">
                                            <ListItemButton onClick={() => setBatteryCapacityOptionsOpen(prevOpen => !prevOpen)} >
                                                <ListItemText primary={t("vehicle-profile.battery-capacity-option")} primaryTypographyProps={{ variant: "body1" }} />
                                                {batteryCapacityOptionsOpen ? <ExpandLess /> : <ExpandMore />}
                                            </ListItemButton>
                                            <Collapse in={batteryCapacityOptionsOpen} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding >
                                                    {batteryCapacity.map((vehicle: Vehicle, key: any) => (
                                                        <ListItemButton key={vehicle.Id} onClick={() => setSelectedCapacityFull([vehicle.CapacityFull!, vehicle.RangeReal!])} dense>
                                                            <div className='list-item-icon-container'>
                                                                <ListItemIcon className='optional-icon'>
                                                                    <Radio color="secondary" checked={vehicle.RangeReal === selectedCapacityFull![1]} />
                                                                </ListItemIcon>
                                                            </div>
                                                            <ListItemText primary={vehicle.CapacityFull + ' kWh (' + vehicle.RangeReal + ' km)'} primaryTypographyProps={{ variant: "subtitle1" }} />
                                                        </ListItemButton>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </List>
                                    </ClickAwayListener>
                                    }
                                    {
                                        (powerMaxFastOptions?.length > 0) &&
                                        <ClickAwayListener onClickAway={() => setPowerMaxFastOptionsOpen(false)}>
                                            <List className="list-option">
                                                <ListItemButton onClick={() => setPowerMaxFastOptionsOpen(prevOpen => !prevOpen)}>
                                                    <ListItemText primary={t("vehicle-profile.fast-charge-option")} primaryTypographyProps={{ variant: "body1" }} />
                                                    {powerMaxFastOptionsOpen ? <ExpandLess /> : <ExpandMore />}
                                                </ListItemButton>
                                                <Collapse in={powerMaxFastOptionsOpen} timeout="auto" unmountOnExit>
                                                    <List component="div" disablePadding >
                                                        {powerMaxFastOptions.map((fastValue: number, key: number) => {
                                                            return (<ListItemButton key={key} onClick={() => setSelectedPowerFast(fastValue)} dense>
                                                                <div className='list-item-icon-container'>
                                                                    <ListItemIcon className='optional-icon'>
                                                                        <Radio color="secondary" checked={fastValue === selectedPowerFast!} />
                                                                    </ListItemIcon>
                                                                </div>
                                                                {(fastValue === 0)
                                                                    ? <ListItemText primary={t("vehicle-profile.no-fast-charge-option")} primaryTypographyProps={{ variant: "subtitle1" }} />
                                                                    : <ListItemText primary={fastValue + ' kW'} primaryTypographyProps={{ variant: "subtitle1" }} />}
                                                            </ListItemButton>)
                                                        })}
                                                    </List>
                                                </Collapse>
                                            </List>
                                        </ClickAwayListener>
                                    }
                                    {
                                        (powerStandardOptions.length > 0) &&
                                        <ClickAwayListener onClickAway={() => setPowerMaxStandardOptionsOpen(false)}>
                                            <List className="list-option">
                                                <ListItemButton onClick={() => setPowerMaxStandardOptionsOpen(prevOpen => !prevOpen)} >
                                                    <ListItemText primary={t("vehicle-profile.standard-charge-option")} primaryTypographyProps={{ variant: "body1" }} />
                                                    {powerMaxStandardOptionsOpen ? <ExpandLess /> : <ExpandMore />}
                                                </ListItemButton>
                                                <Collapse in={powerMaxStandardOptionsOpen} timeout="auto" unmountOnExit>
                                                    <List component="div" disablePadding>
                                                        {powerStandardOptions
                                                            .map((powerStandard: number, key: any) => (
                                                                <ListItemButton key={powerStandard} dense onClick={() => setSelectedPowerStandard(powerStandard)}>
                                                                    <div className='list-item-icon-container'>
                                                                        <ListItemIcon className='optional-icon'>
                                                                            <Radio color="secondary" checked={powerStandard === selectedPowerStandard} />
                                                                        </ListItemIcon>
                                                                    </div>
                                                                    <ListItemText primary={powerStandard + ' kW'} primaryTypographyProps={{ variant: "subtitle1" }} />
                                                                </ListItemButton>
                                                            ))
                                                        }
                                                    </List>
                                                </Collapse>
                                            </List>
                                        </ClickAwayListener>
                                    }
                                </>
                            }
                            <>
                                <div className='connecteur-title'>
                                    <Typography variant="body1" textAlign={"center"} marginTop={2}>{t("vehicle-profile.body2")}</Typography>
                                </div>
                                <div className="vehicle-profile-icon-container">
                                    <div className="list-icons">
                                        <IconProvider id={basicConnectors?.standardPlug} color="grey-filter" size="large" />
                                        <span className='connector-standard'>{t("connector." + basicConnectors?.standardPlug)}</span>
                                    </div>
                                    {(basicConnectors?.fastPlugObject !== undefined && basicConnectors.fastPlugObject.fastPlugOptional === false && basicConnectors.fastPlugObject.fastPlug !== "IEC_62196_T2") &&
                                        <div className="list-icons">
                                            <IconProvider id={basicConnectors.fastPlugObject.fastPlug} color="grey-filter" size="large" />
                                            <span className='connector-standard'>{t("connector." + basicConnectors.fastPlugObject.fastPlug)}</span>
                                        </div>
                                    }
                                    {connectorOptions.map((item: any, key: any) => (
                                        <div key={key} className="list-icons">
                                            <IconProvider id={item} color="grey-filter" size="large" />
                                            <span className='connector-standard'>{t("connector." + item)}</span>
                                        </div>))
                                    }
                                </div>
                                <ClickAwayListener onClickAway={() => setConnectorOptionsOpen(false)}>
                                    <List className="list-option">
                                        <ListItemButton onClick={() => setConnectorOptionsOpen(prevOpen => !prevOpen)} >
                                            <ListItemText primary={t("vehicle-profile.option-title")} primaryTypographyProps={{ variant: "body1" }} />
                                            {connectorOptionsOpen ? <ExpandLess /> : <ExpandMore />}
                                        </ListItemButton>
                                        <Collapse in={connectorOptionsOpen} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding >
                                                {Object.keys(ConnectorList).map((item: any, key: any) => (
                                                    (isConnectorOptional(Object.values(ConnectorList)[key], basicConnectors?.standardPlug, basicConnectors?.fastPlugObject)) ? "" :
                                                        <ListItemButton key={key} onClick={handleConnectorOptionsToggle(Object.values(ConnectorList)[key])} dense>
                                                            <div className='list-item-icon-container'>
                                                                <ListItemIcon className='optional-icon'>
                                                                    <Radio color="secondary" checked={connectorOptions.indexOf(Object.values(ConnectorList)[key]) !== -1} />
                                                                </ListItemIcon>
                                                            </div>
                                                            <div className='list-icons'>
                                                                <IconProvider id={Object.values(ConnectorList)[key]} color="grey-filter" size="small" />
                                                            </div>
                                                            <ListItemText primary={item} primaryTypographyProps={{ variant: "subtitle1" }} />
                                                        </ListItemButton>
                                                ))}
                                            </List>
                                        </Collapse>
                                    </List>
                                </ClickAwayListener>
                            </>
                        </DialogContent>
                        <DialogActions className="dialog-action-container">
                            <Button variant="contained" color="secondary" className="dialog-action-button" onClick={applyOptions}>{t("vehicle-profile.confirm")}</Button>
                        </DialogActions>
                    </Dialog>
                </>
            }
        </>
    )
}